import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Loader2, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Download,
  Upload,
  Link,
  Unlink,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Eye,
  Save,
  Play,
  Search,
  Printer,
  Tag
} from "lucide-react";
import { toast } from "sonner";
import ShelfTalkerModal from "@/components/ShelfTalkerModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AgrinetSync = () => {
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [testingLogin, setTestingLogin] = useState(false);
  const [testingScrape, setTestingScrape] = useState(false);
  const [testScrapeResult, setTestScrapeResult] = useState(null);
  const [updatingCosts, setUpdatingCosts] = useState(false);
  
  // Credentials
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [credentialsSaved, setCredentialsSaved] = useState(false);
  const [loginTested, setLoginTested] = useState(null);
  
  // Data
  const [products, setProducts] = useState([]);
  const [matches, setMatches] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [lastScrape, setLastScrape] = useState(null);
  const [costUpdates, setCostUpdates] = useState([]);
  
  // Stats
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalMatched: 0,
    totalUnmatched: 0
  });

  // Scrape status
  const [scrapeStatus, setScrapeStatus] = useState(null);
  
  // CSV Import state
  const [csvFile, setCsvFile] = useState(null);
  const [importingCsv, setImportingCsv] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState(null);
  
  // Shelf Talker state
  const [showShelfTalkerModal, setShowShelfTalkerModal] = useState(false);
  const [shelfTalkerItems, setShelfTalkerItems] = useState([]);

  useEffect(() => {
    loadCredentials();
    loadData();
    loadScrapeStatus();
  }, []);

  const loadScrapeStatus = async () => {
    try {
      const response = await axios.get(`${API}/agrinet/sync/status`);
      setScrapeStatus(response.data);
      
      // If in progress, keep polling
      if (response.data.status === 'in_progress') {
        setSyncing(true);
        setTimeout(loadScrapeStatus, 2000);
      } else {
        setSyncing(false);
      }
    } catch (error) {
      // Status endpoint not available yet
    }
  };

  const loadCredentials = async () => {
    try {
      const response = await axios.get(`${API}/agrinet/sync/credentials`);
      setEmail(response.data.email || "");
      setCredentialsSaved(response.data.has_password);
    } catch (error) {
      // No credentials saved yet
      setCredentialsSaved(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Load products
      const productsRes = await axios.get(`${API}/agrinet/products`);
      setProducts(productsRes.data.products || []);
      setLastScrape(productsRes.data.last_scrape);
      
      // Load matches
      const matchesRes = await axios.get(`${API}/agrinet/matches`);
      setMatches(matchesRes.data.matches || []);
      
      // Load unmatched
      const unmatchedRes = await axios.get(`${API}/agrinet/products/unmatched`);
      setUnmatched(unmatchedRes.data.unmatched_products || []);
      
      setStats({
        totalProducts: productsRes.data.total || 0,
        totalMatched: matchesRes.data.total || 0,
        totalUnmatched: unmatchedRes.data.total || 0
      });
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!email || !password) {
      toast.error("Please enter both email and password");
      return;
    }
    
    try {
      await axios.post(`${API}/agrinet/sync/credentials`, { email, password });
      toast.success("Credentials saved successfully");
      setCredentialsSaved(true);
      setLoginTested(null);
    } catch (error) {
      toast.error("Failed to save credentials");
    }
  };

  const handleTestLogin = async () => {
    setTestingLogin(true);
    try {
      const response = await axios.post(`${API}/agrinet/sync/test-login`);
      if (response.data.success) {
        toast.success("Login successful!");
        setLoginTested(true);
      } else {
        toast.error(response.data.message || "Login failed");
        setLoginTested(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login test failed");
      setLoginTested(false);
    } finally {
      setTestingLogin(false);
    }
  };

  const handleTestScrape = async () => {
    setTestingScrape(true);
    setTestScrapeResult(null);
    try {
      const response = await axios.post(`${API}/agrinet/sync/test-scrape`);
      setTestScrapeResult(response.data);
      if (response.data.success) {
        toast.success(`Test scrape successful! Price: R${response.data.scraped_price}`);
      } else {
        toast.error(response.data.error || "Test scrape failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Test scrape failed");
      setTestScrapeResult({ success: false, error: error.message });
    } finally {
      setTestingScrape(false);
    }
  };

  const handleSyncPrices = async () => {
    setSyncing(true);
    setScrapeStatus({ status: 'starting', message: 'Starting scrape...' });
    try {
      const response = await axios.post(`${API}/agrinet/sync/scrape`);
      if (response.data.success) {
        toast.success(response.data.message || "Scrape started");
        // Start polling for status
        setTimeout(loadScrapeStatus, 2000);
      } else {
        toast.error(response.data.message || "Could not start scrape");
        setSyncing(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
      setSyncing(false);
    }
  };

  const handleCancelScrape = async () => {
    try {
      await axios.post(`${API}/agrinet/sync/cancel`);
      toast.info("Scrape cancelled");
      setSyncing(false);
      setScrapeStatus({ status: 'cancelled', message: 'Scrape cancelled by user' });
      loadData();
    } catch (error) {
      toast.error("Failed to cancel scrape");
    }
  };

  const handleUpdateCosts = async () => {
    setUpdatingCosts(true);
    try {
      const response = await axios.post(`${API}/agrinet/sync/update-costs`);
      setCostUpdates(response.data.updates || []);
      toast.success(`Updated ${response.data.updated_count} product costs`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update costs");
    } finally {
      setUpdatingCosts(false);
    }
  };

  const handleDeleteMatch = async (agrinetSku) => {
    try {
      await axios.delete(`${API}/agrinet/match/${agrinetSku}`);
      toast.success("Match removed");
      loadData();
    } catch (error) {
      toast.error("Failed to remove match");
    }
  };

  const formatCurrency = (amount) => {
    return `R ${(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    return new Date(dateStr).toLocaleString('en-ZA');
  };

  // CSV Import handler
  const handleCsvImport = async () => {
    if (!csvFile) {
      toast.error("Please select a CSV file first");
      return;
    }
    
    setImportingCsv(true);
    setCsvImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append("file", csvFile);
      
      const response = await axios.post(`${API}/agrinet/import-price-list`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      setCsvImportResult(response.data);
      
      // Prepare items for shelf talker printing (only those with selling price changes)
      const itemsNeedingShelfTalkers = (response.data.updates || [])
        .filter(u => u.needs_shelf_talker && !u.note)
        .map(u => ({
          product_id: u.product_id,
          name: u.product_name,
          plu_code: u.plu_code,
          selling_price: u.new_selling_price,
          print_shelf_talker: true
        }));
      
      setShelfTalkerItems(itemsNeedingShelfTalkers);
      
      if (response.data.updated > 0) {
        const msg = response.data.shelf_talkers_needed > 0
          ? `Updated ${response.data.updated} products. ${response.data.shelf_talkers_needed} need new shelf talkers.`
          : `Updated ${response.data.updated} product costs (no selling price changes)`;
        toast.success(msg);
      } else {
        toast.info("No products were updated - prices may already be current");
      }
      
      // Clear file selection
      setCsvFile(null);
      
    } catch (error) {
      console.error("CSV import error:", error);
      toast.error(error.response?.data?.detail || "Failed to import price list");
    } finally {
      setImportingCsv(false);
    }
  };
  
  // Open shelf talker print dialog
  const handlePrintShelfTalkers = () => {
    if (shelfTalkerItems.length > 0) {
      setShowShelfTalkerModal(true);
    } else {
      toast.info("No products need new shelf talkers");
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="agrinet-sync-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Agrinet Cost Price Sync</h1>
          <p className="text-gray-500">Automatically sync cost prices from Agrinet portal</p>
        </div>
        <Button variant="outline" onClick={loadData} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-500">Last Sync</div>
            <div className="text-lg font-semibold">{formatDate(lastScrape)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-500">Agrinet Products</div>
            <div className="text-2xl font-bold text-blue-600">{stats.totalProducts}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-500">Matched Products</div>
            <div className="text-2xl font-bold text-green-600">{stats.totalMatched}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-sm text-gray-500">Unmatched</div>
            <div className="text-2xl font-bold text-orange-600">{stats.totalUnmatched}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="csv-import" className="space-y-4">
        <TabsList>
          <TabsTrigger value="csv-import">CSV Price Import</TabsTrigger>
          <TabsTrigger value="setup">Portal Sync</TabsTrigger>
          <TabsTrigger value="matches">Matched Products ({stats.totalMatched})</TabsTrigger>
          <TabsTrigger value="updates">Cost Updates</TabsTrigger>
        </TabsList>

        {/* CSV Import Tab */}
        <TabsContent value="csv-import" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="h-5 w-5 text-blue-600" />
                Import Agrinet Price List (CSV)
              </CardTitle>
              <CardDescription>
                Upload the Agrinet price list CSV to update product costs automatically
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                <h4 className="font-semibold text-blue-800 mb-2">Expected CSV Format</h4>
                <div className="grid grid-cols-2 gap-2 text-blue-700">
                  <div><span className="font-medium">Column B:</span> Supplier PLU</div>
                  <div><span className="font-medium">Column E:</span> Description</div>
                  <div><span className="font-medium">Column F:</span> Barcode</div>
                  <div><span className="font-medium">Column H:</span> New Cost (excl VAT)</div>
                  <div><span className="font-medium">Column I:</span> Min Order Qty</div>
                </div>
                <p className="mt-2 text-blue-600">
                  Products are matched by Supplier PLU first, then by Barcode if no PLU match.
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="flex-1 max-w-md">
                  <input
                    type="file"
                    accept=".csv,.CSV,.txt,.TXT"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      console.log("File selected:", file?.name);
                      setCsvFile(file || null);
                    }}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100 cursor-pointer border rounded-lg"
                    id="csv-file-input"
                  />
                </div>
                <Button 
                  onClick={handleCsvImport} 
                  disabled={!csvFile || importingCsv}
                >
                  {importingCsv ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Import Prices
                    </>
                  )}
                </Button>
              </div>
              
              {csvFile && (
                <p className="text-sm text-gray-600">
                  Selected: <span className="font-medium">{csvFile.name}</span> ({(csvFile.size / 1024).toFixed(1)} KB)
                </p>
              )}
              
              {/* Import Results */}
              {csvImportResult && (
                <div className="mt-6 space-y-4">
                  <h4 className="font-semibold text-lg">Import Results</h4>
                  
                  <div className="grid grid-cols-6 gap-4">
                    <Card className="bg-gray-50">
                      <CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold">{csvImportResult.total_rows}</div>
                        <div className="text-sm text-gray-500">Total Rows</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50">
                      <CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold text-green-600">{csvImportResult.matched_by_supplier_plu}</div>
                        <div className="text-sm text-gray-500">Matched by PLU</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50">
                      <CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold text-blue-600">{csvImportResult.matched_by_barcode}</div>
                        <div className="text-sm text-gray-500">Matched by Barcode</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50">
                      <CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold text-purple-600">{csvImportResult.updated}</div>
                        <div className="text-sm text-gray-500">Costs Updated</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-yellow-50 border-yellow-300">
                      <CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold text-yellow-600">{csvImportResult.shelf_talkers_needed || 0}</div>
                        <div className="text-sm text-gray-500">Selling Prices Changed</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-orange-50">
                      <CardContent className="pt-4 text-center">
                        <div className="text-2xl font-bold text-orange-600">{csvImportResult.skipped}</div>
                        <div className="text-sm text-gray-500">Skipped/No Match</div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Print Shelf Talkers Button */}
                  {(csvImportResult.shelf_talkers_needed || 0) > 0 && (
                    <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Tag className="h-6 w-6 text-yellow-600" />
                        <div>
                          <h5 className="font-semibold text-yellow-800">
                            {csvImportResult.shelf_talkers_needed} products need new shelf talkers
                          </h5>
                          <p className="text-sm text-yellow-700">Selling prices have changed - print updated shelf talkers</p>
                        </div>
                      </div>
                      <Button onClick={handlePrintShelfTalkers} className="bg-yellow-600 hover:bg-yellow-700">
                        <Printer className="h-4 w-4 mr-2" />
                        Print Shelf Talkers
                      </Button>
                    </div>
                  )}
                  
                  {csvImportResult.errors?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <h5 className="font-medium text-red-800 mb-2">Errors ({csvImportResult.errors.length})</h5>
                      <ul className="text-sm text-red-700 list-disc list-inside max-h-32 overflow-y-auto">
                        {csvImportResult.errors.slice(0, 10).map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {csvImportResult.updates?.length > 0 && (
                    <div>
                      <h5 className="font-medium mb-2">Price Updates ({csvImportResult.updated})</h5>
                      <div className="max-h-96 overflow-y-auto border rounded-lg">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Product</TableHead>
                              <TableHead>Matched By</TableHead>
                              <TableHead className="text-right">Old Cost</TableHead>
                              <TableHead className="text-right">New Cost</TableHead>
                              <TableHead className="text-right">Old Selling</TableHead>
                              <TableHead className="text-right">New Selling</TableHead>
                              <TableHead className="text-center">Shelf Talker</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {csvImportResult.updates.map((update, idx) => (
                              <TableRow key={idx} className={update.needs_shelf_talker ? 'bg-yellow-50' : ''}>
                                {update.note ? (
                                  <TableCell colSpan={7} className="text-center text-gray-500 italic">
                                    {update.note}
                                  </TableCell>
                                ) : (
                                  <>
                                    <TableCell className="font-medium max-w-xs truncate" title={update.product_name}>
                                      <div>{update.product_name}</div>
                                      <div className="text-xs text-gray-500">{update.plu_code}</div>
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant={update.matched_by === 'supplier_plu' ? 'default' : 'secondary'}>
                                        {update.matched_by === 'supplier_plu' ? 'PLU' : 'Barcode'}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="text-right text-sm">R {update.old_cost?.toFixed(2)}</TableCell>
                                    <TableCell className="text-right text-sm font-medium">
                                      R {update.new_cost?.toFixed(2)}
                                      <span className={`ml-1 text-xs ${update.cost_change >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                                        ({update.cost_change >= 0 ? '+' : ''}{update.cost_change?.toFixed(2)})
                                      </span>
                                    </TableCell>
                                    <TableCell className="text-right text-sm">R {update.old_selling_price?.toFixed(2)}</TableCell>
                                    <TableCell className="text-right text-sm font-medium">
                                      {update.selling_price_updated ? (
                                        <>
                                          R {update.new_selling_price?.toFixed(2)}
                                          <span className="ml-1 text-xs text-red-600">
                                            (+{(update.new_selling_price - update.old_selling_price).toFixed(2)})
                                          </span>
                                        </>
                                      ) : (
                                        <span className="text-gray-400">No change</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="text-center">
                                      {update.needs_shelf_talker ? (
                                        <Badge className="bg-yellow-500">
                                          <Printer className="h-3 w-3 mr-1" />
                                          Print
                                        </Badge>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Setup Tab */}
        <TabsContent value="setup" className="space-y-4">
          <div className="grid grid-cols-2 gap-6">
            {/* Credentials Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Agrinet Portal Login</CardTitle>
                <CardDescription>
                  Enter your Agrinet portal credentials to enable cost price syncing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    placeholder="your-email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="agrinet-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password</Label>
                  <Input
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="agrinet-password-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleSaveCredentials} className="flex-1">
                    <Save className="h-4 w-4 mr-2" />
                    Save Credentials
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleTestLogin}
                    disabled={!credentialsSaved || testingLogin}
                  >
                    {testingLogin ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : loginTested === true ? (
                      <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                    ) : loginTested === false ? (
                      <XCircle className="h-4 w-4 mr-2 text-red-500" />
                    ) : (
                      <Eye className="h-4 w-4 mr-2" />
                    )}
                    Test Login
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={handleTestScrape}
                    disabled={!credentialsSaved || testingScrape}
                    className="border-orange-300 text-orange-700 hover:bg-orange-50"
                  >
                    {testingScrape ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : testScrapeResult?.success ? (
                      <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
                    ) : testScrapeResult && !testScrapeResult.success ? (
                      <XCircle className="h-4 w-4 mr-2 text-red-500" />
                    ) : (
                      <Search className="h-4 w-4 mr-2" />
                    )}
                    Test Scrape
                  </Button>
                </div>
                {/* Test Scrape Results */}
                {testScrapeResult && (
                  <div className={`p-3 rounded-lg text-sm ${testScrapeResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="font-medium mb-2">
                      {testScrapeResult.success ? '✅ Test Scrape Successful' : '❌ Test Scrape Failed'}
                    </div>
                    {testScrapeResult.success && (
                      <div className="text-green-700">
                        Scraped Price: <span className="font-bold">R{testScrapeResult.scraped_price}</span>
                      </div>
                    )}
                    {testScrapeResult.error && (
                      <div className="text-red-700">{testScrapeResult.error}</div>
                    )}
                    {testScrapeResult.result?.test_product && (
                      <div className="mt-2 text-gray-600">
                        Test Product: {testScrapeResult.result.test_product.name} (PLU: {testScrapeResult.result.test_product.supplier_plu})
                      </div>
                    )}
                    {testScrapeResult.result?.steps && (
                      <details className="mt-2">
                        <summary className="cursor-pointer text-gray-500 hover:text-gray-700">View diagnostic steps</summary>
                        <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-auto max-h-48">
                          {JSON.stringify(testScrapeResult.result.steps, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
                {credentialsSaved && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <CheckCircle className="h-4 w-4" />
                    Credentials saved
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sync Actions Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Sync Actions</CardTitle>
                <CardDescription>
                  Run sync operations to fetch and update product costs
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">Step 1: Scrape Prices</h4>
                  <p className="text-sm text-blue-600 mb-3">
                    Fetch latest cost prices from Agrinet for all products with supplier PLU codes
                  </p>
                  <Button 
                    onClick={handleSyncPrices} 
                    disabled={syncing || !credentialsSaved}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="sync-prices-btn"
                  >
                    {syncing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Scraping...
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Scrape Prices from Agrinet
                      </>
                    )}
                  </Button>
                  
                  {/* Progress display */}
                  {scrapeStatus && scrapeStatus.status === 'in_progress' && (
                    <div className="mt-3 p-3 bg-blue-100 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-blue-800">
                          {scrapeStatus.message}
                        </span>
                        <span className="text-sm text-blue-600">
                          {scrapeStatus.processed || 0} / {scrapeStatus.total_products || 0}
                        </span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${scrapeStatus.total_products ? (scrapeStatus.processed / scrapeStatus.total_products * 100) : 0}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between mt-2 text-xs text-blue-600">
                        <span>Found: {scrapeStatus.found || 0}</span>
                        <span>Errors: {scrapeStatus.errors || 0}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Cancel button - show when syncing OR when status is in_progress/starting */}
                  {(syncing || scrapeStatus?.status === 'in_progress') && (
                    <Button 
                      onClick={handleCancelScrape}
                      variant="outline"
                      className="w-full mt-3 text-red-600 border-red-300 hover:bg-red-50"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancel & Reset Scrape
                    </Button>
                  )}
                  
                  {/* Completed status */}
                  {scrapeStatus && scrapeStatus.status === 'completed' && (
                    <div className="mt-3 p-3 bg-green-100 rounded-lg text-sm text-green-800">
                      <CheckCircle className="h-4 w-4 inline mr-2" />
                      {scrapeStatus.message}
                    </div>
                  )}
                  
                  {/* Failed status */}
                  {scrapeStatus && scrapeStatus.status === 'failed' && (
                    <div className="mt-3 p-3 bg-red-100 rounded-lg text-sm text-red-800">
                      <XCircle className="h-4 w-4 inline mr-2" />
                      {scrapeStatus.message || scrapeStatus.error}
                    </div>
                  )}
                </div>

                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="font-medium text-green-800 mb-2">Step 2: Update Costs</h4>
                  <p className="text-sm text-green-600 mb-3">
                    Apply scraped prices to your product database for matched products
                  </p>
                  <Button 
                    onClick={handleUpdateCosts}
                    disabled={updatingCosts || stats.totalMatched === 0}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="update-costs-btn"
                  >
                    {updatingCosts ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Updating Costs...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Update {stats.totalMatched} Product Costs
                      </>
                    )}
                  </Button>
                </div>

                {!credentialsSaved && (
                  <div className="text-sm text-orange-600 bg-orange-50 p-3 rounded-lg">
                    ⚠️ Save your credentials first to enable syncing
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* How It Works */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">How It Works</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg flex-1">
                  <Badge variant="outline">1</Badge>
                  <span>Set supplier to AGRINET on products</span>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg flex-1">
                  <Badge variant="outline">2</Badge>
                  <span>Add Agrinet PLU as Supplier PLU</span>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg flex-1">
                  <Badge variant="outline">3</Badge>
                  <span>Run Scrape Prices to fetch costs</span>
                </div>
                <ArrowRight className="h-4 w-4 text-gray-400" />
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg flex-1">
                  <Badge variant="outline">4</Badge>
                  <span>Run Update Costs to apply changes</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Matches Tab */}
        <TabsContent value="matches">
          <Card>
            <CardHeader>
              <CardTitle>Matched Products</CardTitle>
              <CardDescription>
                Products linked between Agrinet and your inventory
              </CardDescription>
            </CardHeader>
            <CardContent>
              {matches.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No products matched yet. Run a sync to automatically match products.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agrinet SKU</TableHead>
                      <TableHead>Agrinet Name</TableHead>
                      <TableHead>Your PLU</TableHead>
                      <TableHead>Your Product</TableHead>
                      <TableHead>Confidence</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matches.map((match) => (
                      <TableRow key={match.agrinet_sku}>
                        <TableCell className="font-mono">{match.agrinet_sku}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={match.agrinet_name}>
                          {match.agrinet_name}
                        </TableCell>
                        <TableCell className="font-mono">{match.your_plu}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={match.your_product_name}>
                          {match.your_product_name}
                        </TableCell>
                        <TableCell>
                          <Badge variant={match.confidence === 'auto' ? 'default' : 'secondary'}>
                            {match.confidence}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteMatch(match.agrinet_sku)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Unlink className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Updates Tab */}
        <TabsContent value="updates">
          <Card>
            <CardHeader>
              <CardTitle>Cost Update History</CardTitle>
              <CardDescription>
                View the latest cost price changes from Agrinet sync
              </CardDescription>
            </CardHeader>
            <CardContent>
              {costUpdates.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No cost updates yet. Run Update Costs to apply scraped prices.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PLU</TableHead>
                      <TableHead>Product Name</TableHead>
                      <TableHead className="text-right">Old Cost</TableHead>
                      <TableHead className="text-right">New Cost</TableHead>
                      <TableHead className="text-right">Change</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costUpdates.map((update, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono">{update.plu}</TableCell>
                        <TableCell>{update.name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(update.old_cost)}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(update.new_cost)}</TableCell>
                        <TableCell className="text-right">
                          <span className={`flex items-center justify-end gap-1 ${
                            update.change > 0 ? 'text-red-600' : update.change < 0 ? 'text-green-600' : 'text-gray-500'
                          }`}>
                            {update.change > 0 ? (
                              <TrendingUp className="h-4 w-4" />
                            ) : update.change < 0 ? (
                              <TrendingDown className="h-4 w-4" />
                            ) : null}
                            {formatCurrency(Math.abs(update.change))}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Shelf Talker Modal */}
      <ShelfTalkerModal
        open={showShelfTalkerModal}
        onClose={() => setShowShelfTalkerModal(false)}
        items={shelfTalkerItems}
      />
    </div>
  );
};

export default AgrinetSync;
